/**
 * La lista de deudores: la vista «Me deben» de Clientes.
 *
 * Va partida en cajas, una por tramo: los que deben hace mas de un mes, los
 * que deben hace menos y los que tienen saldo a favor (`agruparDeudores`).
 * Desde `md` cada caja es una `<Table>` (06 §3.1: la columna de plata se
 * compara entre filas): Cliente · Mora · Comprobantes · Debe · accion, con el
 * chip de moneda en cada fila y los totales del tramo por moneda de anclaje en
 * el pie (06 §3.5). Bajo `md` son tarjetas-registro, con los mismos totales en
 * una tarjeta al final de cada tramo.
 *
 * Lo que rompia el MASTER y sigue corregido: el saldo en pesos va con el
 * color de su moneda y no en `text-foreground`; "a favor" es un badge y un
 * signo, no verde sobre el importe; la mora es el `BadgeMora` unico.
 *
 * Separada de la ruta para montarla en un test sin router ni queries.
 */
import type { ReactNode } from "react";
import { AlertTriangle, Clock, Wallet, type LucideIcon } from "lucide-react";

import { BadgeMora } from "@/components/cobro/BadgeMora";
import { agruparDeudores, type DeudorFila, type GrupoDeudores } from "@/components/cobro/logica";
import { ChipMoneda, Importe } from "@/components/money/importe";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { SkeletonTabla, SkeletonTarjetas } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { aNumero } from "@/lib/tipos";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Totales
// ─────────────────────────────────────────────────────────────────────────────

export interface TotalesDeudores {
  /** Deuda de las cuentas fijas, en pesos. */
  ars: number;
  /** Deuda de las cuentas atadas al dolar, en dolares. */
  usd: number;
  /** Todo llevado a pesos al dolar de hoy: el unico que mezcla. */
  hoy: number;
  cuentasArs: number;
  cuentasUsd: number;
  enMora: number;
}

/**
 * Los totales van SEPARADOS por moneda de anclaje y nunca sumados entre si.
 * Un total unico de "lo que me deben" mezclaria deuda congelada en pesos con
 * deuda indexada al dolar: el primero no se mueve y el segundo sube mañana.
 * `hoy` es el unico mixto, y donde se muestra dice a que cotizacion.
 */
export function totalizarDeudores(filas: ReadonlyArray<DeudorFila>): TotalesDeudores {
  const t: TotalesDeudores = { ars: 0, usd: 0, hoy: 0, cuentasArs: 0, cuentasUsd: 0, enMora: 0 };
  for (const f of filas) {
    if (f.moneda_anclaje === "USD") {
      t.usd += aNumero(f.saldo);
      t.cuentasUsd += 1;
    } else {
      t.ars += aNumero(f.saldo);
      t.cuentasArs += 1;
    }
    t.hoy += aNumero(f.saldo_ars_hoy);
    if (f.estado_mora !== "AL_DIA") t.enMora += 1;
  }
  return t;
}

export interface ResumenDeudores {
  /** Lo que deben los que deben, al dolar de hoy. Sin restar ningun saldo a favor. */
  debenHoy: number;
  /** Cuantos deben. */
  deudores: number;
  /** Lo que tienen a favor los que tienen a favor, al dolar de hoy, en positivo. */
  aFavorHoy: number;
  /** Cuantos tienen saldo a favor. */
  conAFavor: number;
  /** Cuantos deudores estan en mora. */
  enMora: number;
}

/**
 * Los numeros de arriba de «Me deben».
 *
 * «Te deben hoy» sumaba todos los saldos, y un saldo a favor —plata que el
 * local le debe al cliente— se restaba de lo que le deben al local: con
 * $ 2.984.637 adeudados y $ 114.679 a favor, la tarjeta decia $ 2.869.958.
 * Son dos cosas distintas y van en dos cifras.
 */
export function resumirDeudores(filas: ReadonlyArray<DeudorFila>): ResumenDeudores {
  const r: ResumenDeudores = { debenHoy: 0, deudores: 0, aFavorHoy: 0, conAFavor: 0, enMora: 0 };
  for (const f of filas) {
    const hoy = aNumero(f.saldo_ars_hoy);
    if (hoy > 0.009) {
      r.debenHoy += hoy;
      r.deudores += 1;
      if (f.estado_mora !== "AL_DIA") r.enMora += 1;
    } else if (hoy < -0.009) {
      r.aFavorHoy += -hoy;
      r.conAFavor += 1;
    }
  }
  r.debenHoy = Math.round(r.debenHoy * 100) / 100;
  r.aFavorHoy = Math.round(r.aFavorHoy * 100) / 100;
  return r;
}

// ─────────────────────────────────────────────────────────────────────────────
// La lista
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNAS = 5;

export interface ListaDeudoresProps {
  filas: ReadonlyArray<DeudorFila>;
  onCobrar: (fila: DeudorFila) => void;
  /** Tocar el nombre abre la ficha del cliente. Sin esto, el nombre es texto. */
  onAbrir?: (clienteId: number) => void;
  /** Primera carga, sin datos previos: se muestra el skeleton. */
  cargando?: boolean;
  /** Refetch con datos en pantalla: la lista se atenua, no se vacia. */
  actualizando?: boolean;
  mensajeVacio?: string;
  /** Con filtros activos, el vacio ofrece limpiarlos. */
  onLimpiarFiltros?: () => void;
  /** El `PieDeLista`: cuantos deudores se trajeron de cuantos hay. */
  pie?: ReactNode;
}

export function ListaDeudores({
  filas,
  onCobrar,
  onAbrir,
  cargando = false,
  actualizando = false,
  mensajeVacio = "Nadie te debe nada.",
  onLimpiarFiltros,
  pie,
}: ListaDeudoresProps): JSX.Element {
  const vacio = filas.length === 0;
  const primeraCarga = cargando && vacio;
  const grupos = agruparDeudores(filas);
  const limpiar = onLimpiarFiltros ? (
    <Button type="button" variant="outline" size="sm" onClick={onLimpiarFiltros}>
      Limpiar filtros
    </Button>
  ) : undefined;

  // Sin filtros y sin nadie con saldo: no es un filtro vacio, es una buena noticia.
  if (vacio && !cargando && !onLimpiarFiltros) {
    return <EstadoVacio icono={Wallet} titulo={mensajeVacio} />;
  }

  return (
    <div
      aria-busy={actualizando || undefined}
      className={cn("transition-opacity duration-150", actualizando && "opacity-60")}
    >
      {primeraCarga ? (
        <>
          <Card padding="none" className="hidden overflow-hidden md:block">
            <SkeletonTabla columnas={COLUMNAS} />
          </Card>
          <div className="md:hidden">
            <SkeletonTarjetas />
          </div>
        </>
      ) : vacio ? (
        <>
          <Card padding="none" className="hidden overflow-hidden md:block">
            <Table densidad="normal">
              <EncabezadoTabla />
              <TableBody>
                <TableVacia colSpan={COLUMNAS} mensaje={mensajeVacio} accion={limpiar} />
              </TableBody>
            </Table>
          </Card>
          <div className="md:hidden">
            <EstadoVacio icono={Wallet} titulo={mensajeVacio} accion={limpiar} />
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-4">
          {grupos.map((g) => (
            <CajaTramo key={g.tramo} grupo={g} onCobrar={onCobrar} onAbrir={onAbrir} />
          ))}
          {pie}
        </div>
      )}
    </div>
  );
}

function EncabezadoTabla(): JSX.Element {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>Cliente</TableHead>
        <TableHead>Mora</TableHead>
        <TableHead className="text-right">Comprobantes</TableHead>
        <TableHead className="text-right">Debe</TableHead>
        <TableHead className="text-right">
          <span className="sr-only">Acciones</span>
        </TableHead>
      </TableRow>
    </TableHeader>
  );
}

const ICONO_TRAMO: Record<GrupoDeudores["tramo"], LucideIcon> = {
  VIEJA: AlertTriangle,
  RECIENTE: Clock,
  A_FAVOR: Wallet,
};

/**
 * Una caja por tramo: los que deben hace mas de un mes no se mezclan con los
 * de la semana. Cada caja trae su titulo, su tabla (tarjetas en el celular) y
 * sus propios totales por moneda de anclaje. La de mas de un mes lleva el
 * acento de alerta: es la lista de a quien llamar.
 */
function CajaTramo({
  grupo: g,
  onCobrar,
  onAbrir,
}: {
  grupo: GrupoDeudores;
  onCobrar: (fila: DeudorFila) => void;
  onAbrir?: ((clienteId: number) => void) | undefined;
}): JSX.Element {
  const totales = totalizarDeudores(g.filas);
  const titulo = (
    <TituloSeccion
      icono={ICONO_TRAMO[g.tramo]}
      contador={g.filas.length}
      className={cn(g.tramo === "VIEJA" && "text-warning")}
    >
      {g.titulo}
    </TituloSeccion>
  );

  return (
    <section aria-label={g.titulo} data-tramo={g.tramo}>
      {/* Desktop: la caja con la tabla adentro. */}
      <Card
        padding="none"
        acento={g.tramo === "VIEJA" ? "warning" : "none"}
        className="hidden overflow-hidden md:block"
      >
        <div className="border-b px-4 py-3">{titulo}</div>
        <Table densidad="normal">
          <EncabezadoTabla />
          <TableBody>
            {g.filas.map((f) => (
              <FilaDeudor key={f.cliente_id} fila={f} onCobrar={onCobrar} onAbrir={onAbrir} />
            ))}
          </TableBody>
          {/* Una fila por moneda de anclaje, nunca una suma mixta (MASTER §3.4). */}
          <TableFooter>
            {totales.cuentasArs > 0 ? (
              <TableRow>
                <TableCell colSpan={3}>Total en pesos</TableCell>
                <TableCellImporte monto={totales.ars} moneda="ARS" />
                <TableCell />
              </TableRow>
            ) : null}
            {totales.cuentasUsd > 0 ? (
              <TableRow>
                <TableCell colSpan={3}>Total atado al dólar</TableCell>
                <TableCellImporte monto={totales.usd} moneda="USD" />
                <TableCell />
              </TableRow>
            ) : null}
          </TableFooter>
        </Table>
      </Card>

      {/* Mobile: el titulo, una tarjeta-registro por deudor y los totales. */}
      <div className="flex flex-col gap-2 md:hidden">
        <div className="px-1">{titulo}</div>
        {g.filas.map((f) => (
          <TarjetaDeudor key={f.cliente_id} fila={f} onCobrar={onCobrar} onAbrir={onAbrir} />
        ))}
        <TotalesTarjeta totales={totales} />
      </div>
    </section>
  );
}

interface FilaProps {
  fila: DeudorFila;
  onCobrar: (fila: DeudorFila) => void;
  onAbrir?: ((clienteId: number) => void) | undefined;
}

/** El nombre del deudor: un link a su ficha cuando hay adonde ir. */
function NombreDeudor({ fila: f, onAbrir }: Pick<FilaProps, "fila" | "onAbrir">): JSX.Element {
  if (!onAbrir) return <>{f.nombre}</>;
  return (
    <Button
      type="button"
      variant="link"
      className="h-auto max-w-full justify-start whitespace-normal break-words text-left font-medium text-foreground sm:h-auto sm:truncate"
      onClick={() => onAbrir(f.cliente_id)}
    >
      {f.nombre}
    </Button>
  );
}

function FilaDeudor({ fila: f, onCobrar, onAbrir }: FilaProps): JSX.Element {
  const debe = aNumero(f.saldo_ars_hoy);
  const impagos = aNumero(f.comprobantes_impagos);

  return (
    <TableRow>
      <TableCell className="max-w-[16rem] truncate text-sm font-medium">
        <NombreDeudor fila={f} onAbrir={onAbrir} />
      </TableCell>
      <TableCell>
        <span className="flex flex-wrap items-center gap-1.5">
          <BadgeMora estado={f.estado_mora} corto />
          {f.excedido ? <BadgeExcedido /> : null}
        </span>
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {impagos > 0 ? impagos : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCellImporte>
        <SaldoDeudor fila={f} />
      </TableCellImporte>
      <TableCell className="w-20 text-right">
        {debe > 0.009 ? (
          <Button type="button" size="sm" onClick={() => onCobrar(f)}>
            Cobrar
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

/**
 * Tarjeta-registro (06 §3.4): nombre y mora a la izquierda, el saldo arriba a
 * la derecha con su chip, y Cobrar en la misma linea.
 */
function TarjetaDeudor({ fila: f, onCobrar, onAbrir }: FilaProps): JSX.Element {
  const debe = aNumero(f.saldo_ars_hoy);
  const impagos = aNumero(f.comprobantes_impagos);

  return (
    <Card
      as="article"
      padding="compacto"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:flex-nowrap"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            <NombreDeudor fila={f} onAbrir={onAbrir} />
          </span>
          {f.excedido ? <BadgeExcedido /> : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <BadgeMora estado={f.estado_mora} corto />
          {impagos > 0 ? (
            <span>
              · {f.comprobantes_impagos} {impagos === 1 ? "comprobante" : "comprobantes"}
            </span>
          ) : null}
          {f.moneda_anclaje === "USD" ? (
            <span className="inline-flex items-center gap-1">
              · atada al dólar <ChipMoneda moneda="USD" />
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <SaldoDeudor fila={f} />
      </div>

      {debe > 0.009 ? (
        <Button type="button" size="sm" className="shrink-0" onClick={() => onCobrar(f)}>
          Cobrar
        </Button>
      ) : null}
    </Card>
  );
}

/**
 * El numero siempre en el color de su moneda; el estado va al lado como badge
 * y como signo explicito (MASTER §3.7). Con cuenta atada al dolar, el saldo
 * anclado tambien: son los dos legitimos y hay que ver los dos.
 */
function SaldoDeudor({ fila: f }: { fila: DeudorFila }): JSX.Element {
  const debe = aNumero(f.saldo_ars_hoy);
  if (debe < -0.009) {
    // Una cuenta atada al dolar con saldo a favor lo tiene EN DOLARES: mostrar
    // solo los pesos de hoy decia -$ 47.700 donde la cuenta dice US$ 30, y
    // mañana el numero seria otro sin que nadie haya tocado nada.
    return (
      <span className="inline-flex flex-col items-end gap-0.5">
        <span className="inline-flex items-center gap-2 whitespace-nowrap">
          <Badge variant="success">A favor</Badge>
          <Importe monto={Math.abs(debe)} moneda="ARS" signoExplicito />
        </span>
        {f.moneda_anclaje === "USD" ? (
          <Importe monto={Math.abs(aNumero(f.saldo))} moneda="USD" tamano="sm" signoExplicito />
        ) : null}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <Importe monto={debe} moneda="ARS" />
      {f.moneda_anclaje === "USD" ? (
        <Importe monto={aNumero(f.saldo)} moneda="USD" tamano="sm" />
      ) : null}
    </span>
  );
}

function BadgeExcedido(): JSX.Element {
  return (
    <Badge variant="destructive">
      <AlertTriangle aria-hidden="true" />
      excedido
    </Badge>
  );
}

/** Los mismos totales del pie de la tabla, para la lista de tarjetas. */
function TotalesTarjeta({ totales }: { totales: TotalesDeudores }): JSX.Element {
  return (
    <Card as="section" variant="muted" padding="compacto" className="flex flex-col gap-1 text-sm">
      {totales.cuentasArs > 0 ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Total en pesos</span>
          <Importe monto={totales.ars} moneda="ARS" />
        </div>
      ) : null}
      {totales.cuentasUsd > 0 ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Total atado al dólar</span>
          <Importe monto={totales.usd} moneda="USD" />
        </div>
      ) : null}
    </Card>
  );
}
